import api, { API_BASE } from './api';
import {
    AmendmentRequest,
    ProcurementApprovalRule,
    ProcurementDashboard,
    ProcurementMeta,
    ProcurementSettings,
    ProcurementUserOption,
    PurchaseOrderComment,
    PurchaseOrderDetail,
    PurchaseOrderDocument,
    PurchaseOrderLine,
    PurchaseOrderLinePayload,
    PurchaseOrderListResponse,
    PurchaseOrderPhoto,
    PurchaseOrderPayload,
    PurchaseOrderReceipt,
    ReceiptLine,
    ReceiptLineCondition,
    PurchaseOrderStatus,
} from '../types/procurement.types';

/**
 * Procurement → Purchase Orders API.
 *
 * Purchase orders are branch-scoped, not yard-scoped: the AP team raises them against a
 * branch and the receiving happens at whichever yard the delivery lands at, so nothing here
 * is filtered by `visibleYardIds()`.
 *
 * Receipts follow the Import Orders shape (STOCK.md §6.2): a receipt is created
 * `in_progress`, lines and mandatory photos are attached, and `post` is what applies the
 * quantities to the order. That ordering is what makes "photos are mandatory" enforceable
 * rather than a client-side suggestion.
 */

const query = (params: Record<string, string | undefined>) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== '') q.append(k, v);
    });
    const s = q.toString();
    return s ? `?${s}` : '';
};

/** Streams the export straight to the browser as a file download. */
const downloadExport = async (path: string, format: 'csv' | 'excel', filename: string) => {
    const token = localStorage.getItem('token');
    const base = API_BASE.replace(/\/$/, '');

    const response = await fetch(`${base}${path}${query({ format })}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) throw new Error('Failed to export data');

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'csv'}`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
};

/**
 * A type alias, not an interface: `query()` takes `Record<string, string | undefined>`, and
 * only type aliases get an implicit index signature — an interface here fails to typecheck
 * at every call site.
 */
export type PurchaseOrderListParams = {
    status?: PurchaseOrderStatus | string;
    branchId?: string;
    vendorId?: string;
    /** 'true' → only the caller's own records (the Requester view). */
    mine?: string;
    /** 'open' → only records with an unanswered amendment request. */
    amendment?: string;
    q?: string;
    limit?: string;
    offset?: string;
};

export const purchaseOrdersApi = {
    list: (params: PurchaseOrderListParams = {}) =>
        api.get<PurchaseOrderListResponse>(`/api/procurement/purchase-orders${query(params)}`),

    get: (id: number | string) =>
        api.get<PurchaseOrderDetail>(`/api/procurement/purchase-orders/${id}`),

    create: (body: PurchaseOrderPayload) =>
        api.post<PurchaseOrderDetail>('/api/procurement/purchase-orders', body),

    update: (id: number, body: Partial<PurchaseOrderPayload>) =>
        api.patch<PurchaseOrderDetail>(`/api/procurement/purchase-orders/${id}`, body),

    /** Drafts only — anything past draft is cancelled, never deleted. */
    remove: (id: number) =>
        api.delete<{ message: string }>(`/api/procurement/purchase-orders/${id}`),

    submit: (id: number) =>
        api.post<PurchaseOrderDetail>(`/api/procurement/purchase-orders/${id}/submit`, {}),

    approve: (id: number, note?: string) =>
        api.post<PurchaseOrderDetail>(`/api/procurement/purchase-orders/${id}/approve`, { note }),

    reject: (id: number, reason: string) =>
        api.post<PurchaseOrderDetail>(`/api/procurement/purchase-orders/${id}/reject`, { reason }),

    /** Allocates the PO number — the record is no longer a request after this. */
    issue: (id: number) =>
        api.post<PurchaseOrderDetail>(`/api/procurement/purchase-orders/${id}/issue`, {}),

    cancel: (id: number, reason: string) =>
        api.post<PurchaseOrderDetail>(`/api/procurement/purchase-orders/${id}/cancel`, { reason }),

    /** Writes off every outstanding quantity and lands the PO in Ready to Enter. */
    closeShort: (id: number, reason: string) =>
        api.post<PurchaseOrderDetail>(`/api/procurement/purchase-orders/${id}/close-short`, { reason }),

    enter: (id: number, body: { externalRef: string }) =>
        api.post<PurchaseOrderDetail>(`/api/procurement/purchase-orders/${id}/enter`, {
            ...body, enteredInventorySoftware: true, enteredApprovalMax: true,
        }),

    /** Copies a rejected/cancelled PO into a fresh draft with nothing received. */
    duplicate: (id: number) =>
        api.post<PurchaseOrderDetail>(`/api/procurement/purchase-orders/${id}/duplicate`, {}),

    addLine: (id: number, body: PurchaseOrderLinePayload) =>
        api.post<PurchaseOrderLine>(`/api/procurement/purchase-orders/${id}/lines`, body),

    updateLine: (id: number, lineId: number, body: Partial<PurchaseOrderLinePayload>) =>
        api.patch<PurchaseOrderLine>(`/api/procurement/purchase-orders/${id}/lines/${lineId}`, body),

    removeLine: (id: number, lineId: number) =>
        api.delete<{ message: string }>(`/api/procurement/purchase-orders/${id}/lines/${lineId}`),

    comment: (id: number, body: string) =>
        api.post<PurchaseOrderComment>(`/api/procurement/purchase-orders/${id}/comments`, { body }),

    /**
     * The Yard Manager's only way to ask for a change: they hold no create/update grant on
     * purchase orders, so the request is a comment that lands in the AP queue flagged.
     */
    requestAmendment: (id: number, body: string) =>
        api.post<AmendmentRequest>(`/api/procurement/purchase-orders/${id}/amendments`, { body }),

    resolveAmendment: (id: number, amendmentId: number, resolutionNote: string) =>
        api.post<AmendmentRequest>(
            `/api/procurement/purchase-orders/${id}/amendments/${amendmentId}/resolve`,
            { resolutionNote }),

    dismissAmendment: (id: number, amendmentId: number, resolutionNote: string) =>
        api.post<AmendmentRequest>(
            `/api/procurement/purchase-orders/${id}/amendments/${amendmentId}/dismiss`,
            { resolutionNote }),

    export: (format: 'csv' | 'excel', params: PurchaseOrderListParams = {}) =>
        downloadExport(
            `/api/procurement/purchase-orders/export${query(params)}`, format, 'purchase_orders'),

    /* ------------------------------------------------------------- receipts */

    addReceipt: (id: number, body: {
        receiptDate?: string; docketNumber: string; carrier?: string; notes?: string;
    }) => api.post<PurchaseOrderReceipt>(`/api/procurement/purchase-orders/${id}/receipts`, body),

    getReceipt: (id: number | string, receiptId: number | string) =>
        api.get<PurchaseOrderReceipt>(
            `/api/procurement/purchase-orders/${id}/receipts/${receiptId}`),

    updateReceipt: (id: number, receiptId: number, body: {
        receiptDate?: string; docketNumber?: string; carrier?: string; notes?: string;
    }) => api.patch<PurchaseOrderReceipt>(
        `/api/procurement/purchase-orders/${id}/receipts/${receiptId}`, body),

    addReceiptLine: (id: number, receiptId: number, body: {
        purchaseOrderLineId: number; quantityReceived: number;
        condition: ReceiptLineCondition; notes?: string;
    }) => api.post<ReceiptLine>(
        `/api/procurement/purchase-orders/${id}/receipts/${receiptId}/lines`, body),

    updateReceiptLine: (id: number, receiptId: number, lineId: number, body: {
        quantityReceived?: number; condition?: ReceiptLineCondition; notes?: string;
    }) => api.patch<ReceiptLine>(
        `/api/procurement/purchase-orders/${id}/receipts/${receiptId}/lines/${lineId}`, body),

    deleteReceiptLine: (id: number, receiptId: number, lineId: number) =>
        api.delete<{ message: string }>(
            `/api/procurement/purchase-orders/${id}/receipts/${receiptId}/lines/${lineId}`),

    /**
     * Multipart via fetch so the browser sets its own boundary; the file is hashed
     * server-side (STOCK.md §12.1). One file per request — callers batching a multi-pick
     * loop over the files.
     */
    uploadReceiptPhoto: async (
        id: number, receiptId: number, file: File, geo?: { lat?: number; lng?: number },
    ) => {
        const token = localStorage.getItem('token');
        const form = new FormData();
        form.append('photo', file);
        form.append('stage', 'receipt');
        form.append('deviceTimestamp', new Date().toISOString());
        if (geo?.lat !== undefined) form.append('lat', String(geo.lat));
        if (geo?.lng !== undefined) form.append('lng', String(geo.lng));

        const response = await fetch(
            `${API_BASE.replace(/\/$/, '')}/api/procurement/purchase-orders/${id}/receipts/${receiptId}/photos`,
            { method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {}, body: form });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data?.message || 'Failed to upload photo');
        return data as PurchaseOrderPhoto;
    },

    deleteReceiptPhoto: (id: number, receiptId: number, photoId: number) =>
        api.delete<{ message: string }>(
            `/api/procurement/purchase-orders/${id}/receipts/${receiptId}/photos/${photoId}`),

    uploadReceiptDocument: async (id: number, receiptId: number, file: File) => {
        const token = localStorage.getItem('token');
        const form = new FormData();
        form.append('file', file);

        const response = await fetch(
            `${API_BASE.replace(/\/$/, '')}/api/procurement/purchase-orders/${id}/receipts/${receiptId}/documents`,
            { method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {}, body: form });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data?.message || 'Failed to upload document');
        return data as PurchaseOrderDocument;
    },

    deleteReceiptDocument: (id: number, receiptId: number, documentId: number) =>
        api.delete<{ message: string }>(
            `/api/procurement/purchase-orders/${id}/receipts/${receiptId}/documents/${documentId}`),

    /* -------------------------------------------------- order-level documents */

    /** A quotation or other order paperwork. Multipart, one file per request. */
    uploadDocument: async (id: number, file: File) => {
        const token = localStorage.getItem('token');
        const form = new FormData();
        form.append('file', file);

        const response = await fetch(
            `${API_BASE.replace(/\/$/, '')}/api/procurement/purchase-orders/${id}/documents`,
            { method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {}, body: form });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data?.message || 'Failed to upload document');
        return data as PurchaseOrderDocument;
    },

    renameDocument: (id: number, documentId: number, newFilename: string) =>
        api.patch<PurchaseOrderDocument>(
            `/api/procurement/purchase-orders/${id}/documents/${documentId}/rename`,
            { newFilename }),

    deleteDocument: (id: number, documentId: number) =>
        api.delete<{ message: string }>(
            `/api/procurement/purchase-orders/${id}/documents/${documentId}`),

    /** Server refuses until the mandatory photo count is met. */
    postReceipt: (id: number, receiptId: number) =>
        api.post<PurchaseOrderDetail>(
            `/api/procurement/purchase-orders/${id}/receipts/${receiptId}/post`, {}),

    voidReceipt: (id: number, receiptId: number, reason: string) =>
        api.post<PurchaseOrderDetail>(
            `/api/procurement/purchase-orders/${id}/receipts/${receiptId}/void`, { reason }),
};

export const procurementDashboardApi = {
    get: () => api.get<ProcurementDashboard>('/api/procurement/dashboard'),
};

export const procurementMetaApi = {
    get: () => api.get<ProcurementMeta>('/api/procurement/meta'),
};

export const procurementSettingsApi = {
    get: () => api.get<ProcurementSettings>('/api/procurement/settings'),

    update: (body: { minReceiptPhotos?: number; photoHelperText?: string }) =>
        api.patch<ProcurementSettings>('/api/procurement/settings', body),

    saveApprovalRules: (rules: Omit<ProcurementApprovalRule, 'branchName'>[]) =>
        api.put<ProcurementSettings>(
            '/api/procurement/settings/approval-rules', { rules }),

    /** The whole list, not a delta: the server replaces what is there with what is sent. */
    saveUserBranches: (allocations: { userId: number; branchId: number }[]) =>
        api.put<ProcurementSettings>(
            '/api/procurement/settings/user-branches', { allocations }),

    /** The notification matrix: event id → the user ids selected for it. */
    saveNotifications: (notifications: Record<string, number[]>) =>
        api.put<ProcurementSettings>(
            '/api/procurement/settings/notifications', { notifications }),

    /**
     * Users for the allocation picker. `/api/users/selection` is the app's existing people
     * picker projection (id, name, email) and is deliberately not permission-gated, so it
     * works for the Admin who is the only role that reaches this screen.
     */
    listUsers: async () => {
        const data = await api.get<{ success: boolean; users: ProcurementUserOption[] }>(
            '/api/users/selection');
        return data.users ?? [];
    },
};
